/**
 * Google tag (GA4 + Google Ads) loader, gated on consent.
 *
 * Consent Mode v2 in *basic* mode: nothing from Google is requested until the
 * visitor has agreed. When they do, the denied defaults are pushed onto the
 * dataLayer *before* the tag is fetched, so the tag never runs in an unknown
 * state, and are then updated to the actual decision.
 *
 * Both properties live on the single GA4 container, which is why there is one
 * script request instead of two.
 */
import {
    ANALYTICS_ENABLED,
    GA4_MEASUREMENT_ID,
    GOOGLE_ADS_ID,
    GOOGLE_COOKIE_PREFIXES,
    GTAG_SRC,
} from './config';
import { deleteCookie } from './store';

let scriptInjected = false;
const configured = new Set();

function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
}

function ensureGtag() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') window.gtag = gtag;
}

/**
 * One decision drives every Google signal: the Ads tag shares this container, so
 * consenting to it grants the ad_* storage types alongside analytics_storage.
 */
function consentSignals({ googleAnalytics }) {
    const google = googleAnalytics ? 'granted' : 'denied';
    return {
        ad_storage: google,
        ad_user_data: google,
        ad_personalization: google,
        personalization_storage: google,
        analytics_storage: google,
        functionality_storage: 'granted',
        security_storage: 'granted',
    };
}

/**
 * Loads and/or reconfigures the Google tag for the given decision.
 * Idempotent — safe to call on every consent change.
 */
export function bootGoogle(state) {
    if (!ANALYTICS_ENABLED) return;
    if (!state.googleAnalytics) return;

    ensureGtag();

    if (!scriptInjected) {
        window.gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            personalization_storage: 'denied',
            analytics_storage: 'denied',
            functionality_storage: 'granted',
            security_storage: 'granted',
        });

        const script = document.createElement('script');
        script.async = true;
        script.src = GTAG_SRC;
        document.head.appendChild(script);
        window.gtag('js', new Date());
        scriptInjected = true;
    }

    window.gtag('consent', 'update', consentSignals(state));

    if (!configured.has(GA4_MEASUREMENT_ID)) {
        window.gtag('config', GA4_MEASUREMENT_ID, {
            // Gatsby is a single-page app: page views are sent from onRouteUpdate
            // instead, otherwise the first one is counted twice.
            send_page_view: false,
            // Ad features are part of the same decision as analytics here.
            allow_google_signals: true,
        });
        configured.add(GA4_MEASUREMENT_ID);
    }

    if (!configured.has(GOOGLE_ADS_ID)) {
        window.gtag('config', GOOGLE_ADS_ID);
        configured.add(GOOGLE_ADS_ID);
    }
}

/** Sends a GA4 page_view. No-op unless the Google tag was consented to. */
export function trackGooglePageView(location) {
    if (!ANALYTICS_ENABLED) return;
    if (!configured.has(GA4_MEASUREMENT_ID)) return;
    window.gtag('event', 'page_view', {
        page_path: location ? location.pathname + location.search + location.hash : undefined,
        page_location: window.location.href,
        page_title: document.title,
    });
}

/**
 * Withdraws consent: tells the tag to stop storing, then removes the cookies it
 * already wrote. The tag itself can only be unloaded by a reload — the caller
 * does that.
 */
export function revokeGoogle() {
    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', consentSignals({ googleAnalytics: false }));
    }
    for (const raw of document.cookie.split(';')) {
        const name = raw.split('=')[0].trim();
        if (!name) continue;
        if (GOOGLE_COOKIE_PREFIXES.some(prefix => name.startsWith(prefix))) {
            deleteCookie(name);
        }
    }
}
